import {
  createContext,
  useContext,
  type Dispatch,
  type SetStateAction,
} from 'react';

/** Shape of `activityAction` when opening/editing a stage activity from the interview plan. */
export type InterviewStageActivityAction = {
  type?: string;
  stage?: { id?: number | string; [key: string]: unknown };
  activity?: { id?: number | string; [key: string]: unknown };
};

export interface InterviewStageManagerValue {
  interviewPlan: unknown;
  activityDisclosure: {
    isOpen: boolean;
    onOpen: () => void;
    onClose: () => void;
  };
  setInterviewPlan: (_plan: unknown) => void;
  selectedActivity: string;
  setSelectedActivity: (_value: string) => void;
  activityAction: InterviewStageActivityAction;
  setActivityAction: Dispatch<SetStateAction<InterviewStageActivityAction>>;
  isSettings: boolean;
  /**
   * Standalone Settings → Interview Plans only: edits a template, which never
   * holds candidates. NOT `isSettings` (that's the per-job settings sub-tab).
   */
  isInterviewPlanTemplate: boolean;
  disableActivity: boolean;
  jobId: unknown;
  refresh: () => void;
  canEditJob: boolean;
  scorecardParams: Record<string, unknown>;
}

const noop = () => {};

const defaultContextValue: InterviewStageManagerValue = {
  interviewPlan: null,
  activityDisclosure: { isOpen: false, onOpen: noop, onClose: noop },
  setInterviewPlan: noop,
  selectedActivity: 'select_activity',
  setSelectedActivity: noop,
  activityAction: {},
  setActivityAction: noop as Dispatch<
    SetStateAction<InterviewStageActivityAction>
  >,
  isSettings: false,
  isInterviewPlanTemplate: false,
  disableActivity: false,
  jobId: null,
  refresh: noop,
  canEditJob: false,
  scorecardParams: {},
};

const InterviewStageManager =
  createContext<InterviewStageManagerValue>(defaultContextValue);

InterviewStageManager.displayName = 'InterviewStageManager';

export const useInterviewStageManager = () => useContext(InterviewStageManager);

export const InterviewStageProvider = InterviewStageManager.Provider;
